import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { useTrackFirstView } from '@ifixit/analytics/hooks/use-track-first-view';
import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { useTranslations } from '@ifixit/i18n/hooks';
import { Duration } from '@ifixit/utils/duration';
import { useCacheableQuery } from '@ifixit/utils-react/hooks';
import { Badge } from '@ifixit/ui/components/badge';
import { Button } from '@ifixit/ui/components/button';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { useRef, useState } from 'react';
import { z } from 'zod';

const MAX_DISTANCE_MILES = 30;
const eventCategory = 'Repair Shop Locator';

const RepairShopSchema = z.object({
   name: z.string(),
   address: z.string(),
   city: z.string(),
   state: z.string(),
   postcode: z.string(),
   country: z.string(),
   url: z.string(),
   consultationUrl: z.string(),
   latitude: z.number(),
   longitude: z.number(),
   distanceMiles: z.number(),
});
type RepairShop = z.infer<typeof RepairShopSchema>;

export type RepairShopLocatorProps = {
   imageUrl: string;
};

type Coordinates = Pick<GeolocationCoordinates, 'latitude' | 'longitude'>;

export function RepairShopLocator({ imageUrl }: RepairShopLocatorProps) {
   const [open, setIsOpen] = useState(false);
   const [coords, setCoords] = useState<Coordinates | null>(debugCoords());
   const closestShop = useClosestRepairShop({
      latitude: coords?.latitude ?? null,
      longitude: coords?.longitude ?? null,
   });
   const firstClosestShop = useFirstDefined(closestShop);
   const t = useTranslations('GuideProducts.repairShopLocator');
   const ref = useTrackFirstView<HTMLDivElement>({ eventCategory, eventAction: 'View' });

   const onClick = () => {
      setIsOpen(true);
      trackEvent({
         closestShop,
         eventAction: 'Find Repair Shop Click',
      });
      navigator.geolocation.getCurrentPosition(
         position => {
            setCoords(position.coords);
         },
         positionError => {
            console.error(
               'Failed to get current position for repair shop locator',
               positionError.code,
               positionError.message
            );
         },
         { enableHighAccuracy: true, timeout: 5000, maximumAge: 3_600_000 }
      );
   };

   const hide = firstClosestShop == null || firstClosestShop.distanceMiles > MAX_DISTANCE_MILES;

   if (hide) {
      return null;
   }

   return (
      <div
         className="mb-4 rounded-xl w-full overflow-hidden grid grid-rows-2 md:grid-cols-2 md:grid-rows-none"
         ref={ref}
      >
         <div className="p-4 flex flex-col items-start justify-center gap-4 bg-gray-100 md:py-8">
            <h3>{t('title')}</h3>
            <p>{t('description')}</p>
            <Button className="button-outline" onClick={onClick}>
               {t('cta')}
            </Button>
         </div>
         <div className="relative overflow-hidden">
            {open ? (
               <RepairShopCard closestShop={closestShop} setIsOpen={setIsOpen} />
            ) : (
               <img src={imageUrl} alt="" className="absolute inset-0 size-full object-cover" />
            )}
         </div>
      </div>
   );
}

type RepairShopCardProps = {
   closestShop: RepairShop | null;
   setIsOpen: (open: boolean) => void;
};

function RepairShopCard({ closestShop, setIsOpen }: RepairShopCardProps) {
   const t = useTranslations('GuideProducts.repairShopLocator');

   const scheduleNowOnClick = () => {
      trackEvent({
         closestShop,
         eventAction: 'Schedule Now Click',
      });
   };
   const storeDetailsOnClick = () => {
      trackEvent({
         closestShop,
         eventAction: 'Store Details Click',
      });
   };

   return (
      <div
         className={cn(
            'relative p-4 flex flex-col size-full justify-center gap-4 bg-white',
            'rounded-br-xl rounded-bl-xl md:rounded-bl-none md:rounded-tr-xl',
            'border-r border-l border-b border-gray-100 md:border-l-0 md:border-t'
         )}
      >
         <Button
            className="button-ghost absolute top-2 right-2 text-gray-500"
            onClick={() => {
               setIsOpen(false);
            }}
         >
            <FaIcon name="xmark" />
         </Button>
         {closestShop ? (
            <>
               {/* Override Guide/UI/templates/css/Guide/View/v2/structure.less */}
               <h3 className="mr-4! text-gray-500!">{t('resultTitle').toLocaleUpperCase()}</h3>
               <div>
                  <div className="flex items-center gap-4">
                     <h4 className="text-popover-foreground font-semibold">{closestShop.name}</h4>
                     <Badge className="badge-brand">
                        {t('miles', { count: Math.round(closestShop.distanceMiles * 10) / 10 })}
                     </Badge>
                  </div>
                  <div className="flex items-start gap-2">
                     <div className="mt-px">📍</div>
                     <div>
                        <div>{closestShop.address}</div>
                        <div>
                           {closestShop.city}, {closestShop.state} {closestShop.postcode}
                        </div>
                     </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                     <Button
                        asChild
                        className="no-underline hover:no-underline active:no-underline"
                     >
                        <a
                           href={closestShop.consultationUrl}
                           target="_blank"
                           rel="noreferrer"
                           onClick={scheduleNowOnClick}
                        >
                           {t('scheduleNow')}
                        </a>
                     </Button>
                     <Button
                        asChild
                        className="button-outline no-underline hover:no-underline active:no-underline"
                     >
                        <a
                           href={closestShop.url}
                           target="_blank"
                           rel="noreferrer"
                           onClick={storeDetailsOnClick}
                        >
                           {t('storeDetails')}
                        </a>
                     </Button>
                  </div>
               </div>
            </>
         ) : (
            <>
               <div className="w-[90%] h-6 rounded-xl bg-gray-100 animate-pulse" />
               <div className="w-full h-36 rounded-xl bg-gray-100 animate-pulse" />
            </>
         )}
      </div>
   );
}

type UseClosestRepairShopProps = {
   latitude: number | null;
   longitude: number | null;
};

function useClosestRepairShop({ latitude, longitude }: UseClosestRepairShopProps) {
   const api = useIFixitApiClient();

   const { data, error } = useCacheableQuery({
      cacheTime: Duration(1).day,
      schema: RepairShopSchema.nullable(),
      storage: 'localStorage',
      queryKey: ['nearest-repair-shop', latitude, longitude],
      queryFn: async () => {
         const coordinates =
            latitude === null || longitude === null ? undefined : { latitude, longitude };
         return api.post<RepairShop | null>(
            'repair-shop-locator/nearest',
            'get-nearest-repair-shop',
            { body: JSON.stringify({ coordinates }) }
         );
      },
   });

   if (error) {
      console.error(error.name, error.message, error.stack);
   }

   return data ?? null;
}

function useFirstDefined<T>(value: T): NonNullable<T> | null {
   const ref = useRef<NonNullable<T> | null>(null);

   if (value != null && ref.current == null) {
      ref.current = value;
   }

   return ref.current;
}

type TrackEventProps = {
   closestShop: RepairShop | null;
   eventAction: string;
};

function trackEvent({ closestShop, eventAction }: TrackEventProps) {
   trackAnalyticsCustomEvent({
      eventCategory: 'Repair Shop Locator',
      eventAction,
      eventName: `${closestShop?.name}, ${closestShop?.city}`,
   });
}

function debugCoords() {
   const searchParams = new URLSearchParams(window.location.search);
   const latitude = Number(searchParams.get('latitude') ?? 'nan');
   const longitude = Number(searchParams.get('longitude') ?? 'nan');
   if (isNaN(latitude) || isNaN(longitude)) return null;
   return { latitude, longitude };
}
